// src/screens/admin/CoaMapper.tsx
// Standalone admin screen: maps the connected Xero tenant's chart of accounts
// onto the fixed FFM category set. Saves the mapping to Redis per tenant.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FFM_CATEGORY_KEYS,
  FFM_CATEGORIES,
  FFMCategory,
  PL_GROUPS,
  suggestCategory,
  SuggestionResult,
} from '../../config/ffmCategories';

interface XeroAccount {
  accountId: string;
  code: string;
  name: string;
  type: string;
  class?: string;
  status?: string;
}

type FilterMode = 'all' | 'low_confidence' | 'unmapped' | 'mapped';

const SOURCE_LABEL: Record<SuggestionResult['source'], string> = {
  type: 'TYPE',
  code: 'CODE',
  keyword: 'NAME',
  default: 'DEFAULT',
};

const SOURCE_BG: Record<SuggestionResult['source'], string> = {
  type: '#DCFCE7',
  code: '#DBEAFE',
  keyword: '#FEF3C7',
  default: '#F3F4F6',
};

const SOURCE_FG: Record<SuggestionResult['source'], string> = {
  type: '#166534',
  code: '#1E40AF',
  keyword: '#92400E',
  default: '#4B5563',
};

function confidenceColour(c: number): string {
  if (c >= 0.85) return '#10B981';
  if (c >= 0.65) return '#F59E0B';
  return '#EF4444';
}

function isOwnerLike(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes('director') || n.includes('owner');
}

export default function CoaMapper() {
  const [accounts, setAccounts] = useState<XeroAccount[]>([]);
  const [mapping, setMapping] = useState<Record<string, FFMCategory>>({});
  const [suggestions, setSuggestions] = useState<Record<string, SuggestionResult>>({});
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string>('');
  const [tenantId, setTenantId] = useState<string>('');
  const [availableTenants, setAvailableTenants] = useState<Array<{ tenantId: string; tenantName: string }>>([]);
  const [filter, setFilter] = useState<FilterMode>('all');
  const [search, setSearch] = useState('');
  // Save-success toast: "Mapping saved — N accounts mapped to M categories"
  // auto-dismissed after 3 seconds. Null means no toast visible.
  const [saveToast, setSaveToast] = useState<{ accounts: number; categories: number } | null>(null);

  // ─── Loader (reused for both initial load and re-run) ─────────────────────
  // Three modes:
  //   default (initial mount) — fresh accounts as source of truth; for each
  //     fresh account, use saved category from Redis if present, else seed
  //     from suggestCategory(). This is the "merge" behaviour from CHANGE 1
  //     that ensures the displayed list reflects the live /api/xero/accounts
  //     response (P&L-only post-filter), never the stale 187-row Redis
  //     mapping.
  //   forceResuggest=true — re-run engine: fresh accounts source of truth,
  //     but ignore BOTH the saved mapping and any in-memory adviser choices.
  //     Every account gets a fresh suggestCategory() result. This is what
  //     the "Re-run suggestions" button invokes.
  //   preserveMapping=true (unused today, kept for completeness) — keep the
  //     adviser's in-memory choices for accounts still present.
  const loadData = useCallback(
    async (
      opts: {
        preserveMapping?: boolean;
        forceResuggest?: boolean;
        tenantOverride?: { tenantId: string; tenantName: string };
      } = {},
    ): Promise<boolean> => {
      try {
        setError(null);

        // 1. Resolve tenant. Priority:
        //    a) explicit override from the dropdown handler (avoids stale
        //       closure on tenantId state right after setTenantId);
        //    b) tenantId already in state from a previous load;
        //    c) first authorised tenant from /api/xero/tenants.
        let tId = opts.tenantOverride?.tenantId || tenantId;
        let tName = opts.tenantOverride?.tenantName || tenantName;
        if (!tId) {
          const tenantsRes = await fetch('/api/xero/tenants');
          const tenantsJson = await tenantsRes.json();
          const tenants: Array<{ tenantId: string; tenantName: string }> =
            tenantsJson.tenants || [];
          if (tenants.length === 0) {
            throw new Error('No Xero tenants connected. Connect on the main app first.');
          }
          setAvailableTenants(tenants);
          tId = tenants[0].tenantId;
          tName = tenants[0].tenantName;
          setTenantId(tId);
          setTenantName(tName);
        }

        // 2. Fresh accounts — this is the source of truth for which rows
        //    get rendered. Any saved-but-now-filtered-out account simply
        //    never appears in the loop below.
        const accRes = await fetch(`/api/xero/accounts?tenantId=${encodeURIComponent(tId)}`);
        if (!accRes.ok) {
          const j = await accRes.json().catch(() => ({}));
          throw new Error(j.error || `Accounts fetch failed (${accRes.status})`);
        }
        const accJson = await accRes.json();
        const accs: XeroAccount[] = accJson.accounts || [];

        // 3. Saved mapping — only consulted when NOT force-resuggesting.
        //    On a re-run we deliberately skip this so Peter sees fresh
        //    suggestions independent of his saved choices.
        let savedMap: Record<string, FFMCategory> | null = null;
        if (!opts.forceResuggest) {
          const mapRes = await fetch(
            `/api/xero/mapping-get?tenantId=${encodeURIComponent(tId)}`,
          );
          const mapJson = await mapRes.json();
          savedMap = mapJson.mapping || null;
        }

        // 4. Merge: iterate FRESH accounts (not savedMap). This guarantees
        //    removed/filtered accounts disappear and new accounts appear.
        const sugg: Record<string, SuggestionResult> = {};
        const nextMap: Record<string, FFMCategory> = {};
        for (const a of accs) {
          const s = suggestCategory(a.name || '', a.type || '', a.code || '');
          sugg[a.accountId] = s;
          if (opts.forceResuggest) {
            // Re-run mode: always use the freshly-computed suggestion.
            nextMap[a.accountId] = s.category;
          } else if (opts.preserveMapping && mapping[a.accountId]) {
            // Preserve the adviser's in-memory choice.
            nextMap[a.accountId] = mapping[a.accountId];
          } else if (savedMap && savedMap[a.accountId]) {
            // Fall back to the previously-saved mapping.
            nextMap[a.accountId] = savedMap[a.accountId];
          } else {
            // Seed from the auto-suggestion.
            nextMap[a.accountId] = s.category;
          }
        }

        setAccounts(accs);
        setSuggestions(sugg);
        setMapping(nextMap);
        return true;
      } catch (err: any) {
        setError(err?.message || 'Failed to load accounts');
        return false;
      }
    },
    [mapping],
  );

  // ─── On mount: fetch status, accounts, saved mapping ──────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Initial load never preserves in-memory mapping (there is none).
      await loadData({ preserveMapping: false });
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // Intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Sorted + filtered list ───────────────────────────────────────────────
  const visibleAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = accounts.filter((a) => {
      const s = suggestions[a.accountId];
      const conf = s ? s.confidence : 0;
      if (filter === 'low_confidence' && conf >= 0.65) return false;
      if (filter === 'unmapped' && mapping[a.accountId] && mapping[a.accountId] !== 'other_operating') return false;
      if (filter === 'mapped' && (!mapping[a.accountId] || mapping[a.accountId] === 'other_operating')) return false;
      if (q) {
        const hay = `${a.code} ${a.name} ${a.type}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return filtered.sort((x, y) => {
      const cx = suggestions[x.accountId]?.confidence ?? 0;
      const cy = suggestions[y.accountId]?.confidence ?? 0;
      if (cx !== cy) return cx - cy; // lowest confidence first
      return (x.code || '').localeCompare(y.code || '');
    });
  }, [accounts, suggestions, mapping, filter, search]);

  // ─── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = accounts.length;
    let lowConf = 0;
    let highConf = 0;
    for (const a of accounts) {
      const c = suggestions[a.accountId]?.confidence ?? 0;
      if (c >= 0.85) highConf += 1;
      else if (c < 0.65) lowConf += 1;
    }
    return { total, lowConf, highConf };
  }, [accounts, suggestions]);

  // ─── Group counts for summary ─────────────────────────────────────────────
  const groupCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const cat of FFM_CATEGORY_KEYS) counts[cat] = 0;
    for (const a of accounts) {
      const c = mapping[a.accountId];
      if (c) counts[c] = (counts[c] || 0) + 1;
    }
    return counts;
  }, [accounts, mapping]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  function setCategory(accountId: string, cat: FFMCategory) {
    setMapping((m) => ({ ...m, [accountId]: cat }));
    setSaved(false);
  }

  async function handleRerun() {
    // Re-run deliberately ignores the saved mapping so the adviser can see
    // what the current suggestion engine would do from scratch. Any
    // in-memory edits are also overwritten; the adviser can still hit
    // "Save mapping" afterwards (or not) to commit.
    setRerunning(true);
    setSaved(false);
    await loadData({ forceResuggest: true });
    setRerunning(false);
  }

  async function handleSave() {
    if (!tenantId) {
      setError('No tenantId — cannot save');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const res = await fetch('/api/xero/mapping-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, mapping }),
      });
      const json = await res.json();
      if (!res.ok || !json.saved) {
        throw new Error(json.error || `Save failed (${res.status})`);
      }
      setSaved(true);
      // Compute the banner counts from the currently-saved mapping:
      //   N = number of accounts with a non-ignore, non-default category
      //   M = number of distinct FFM categories represented
      const mappedAccounts = Object.entries(mapping).filter(
        ([, c]) => c && c !== 'ignore',
      );
      const distinctCategories = new Set(mappedAccounts.map(([, c]) => c));
      setSaveToast({
        accounts: mappedAccounts.length,
        categories: distinctCategories.size,
      });
      setTimeout(() => setSaved(false), 2000);
      setTimeout(() => setSaveToast(null), 3000);
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading chart of accounts…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* HEADER */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-ma-navy">Chart of Accounts Mapper</h1>
              <p className="text-sm text-gray-500 mt-1">Map every Xero nominal to one FFM category.</p>
              {availableTenants.length > 1 ? (
                <select
                  className="mt-2 text-sm border border-gray-300 rounded px-2 py-1"
                  value={tenantId}
                  onChange={async (e) => {
                    const next = availableTenants.find((t) => t.tenantId === e.target.value);
                    if (!next) return;
                    setTenantId(next.tenantId);
                    setTenantName(next.tenantName);
                    setLoading(true);
                    await loadData({ preserveMapping: false, tenantOverride: next });
                    setLoading(false);
                  }}
                >
                  {availableTenants.map((t) => (
                    <option key={t.tenantId} value={t.tenantId}>{t.tenantName}</option>
                  ))}
                </select>
              ) : tenantName ? (
                <p className="text-sm font-medium text-ma-navy mt-1">{tenantName}</p>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              {/* Re-run suggestions — secondary/outlined button. Refetches the
                  Xero chart of accounts and re-applies suggestCategory() to
                  every row, IGNORING any saved mapping so the adviser sees
                  the current engine's fresh output from scratch. */}
              <button
                onClick={handleRerun}
                disabled={rerunning || saving}
                className="px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50"
                style={{
                  background: 'white',
                  color: '#001F8C',
                  border: '1px solid #001F8C',
                  cursor: rerunning || saving ? 'wait' : 'pointer',
                  minWidth: '160px',
                }}
                title="Re-fetch Xero accounts and re-run the auto-suggestion engine from scratch (overwrites in-memory edits)"
              >
                {rerunning ? 'Running…' : 'Re-run suggestions'}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || rerunning}
                className="px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50"
                style={{
                  background: saved ? '#10B981' : '#001F8C',
                  color: 'white',
                  cursor: saving ? 'wait' : 'pointer',
                  minWidth: '160px',
                }}
              >
                {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save mapping'}
              </button>
            </div>
          </div>

          {/* Save-success toast banner — auto-dismisses after 3s. */}
          {saveToast && (
            <div
              className="mb-4 p-3 rounded-lg text-sm flex items-center gap-2"
              style={{
                background: '#ECFDF5',
                border: '1px solid #A7F3D0',
                color: '#065F46',
              }}
              role="status"
              aria-live="polite"
            >
              <span aria-hidden>✓</span>
              <span>
                Mapping saved — <strong>{saveToast.accounts}</strong> account
                {saveToast.accounts === 1 ? '' : 's'} mapped to{' '}
                <strong>{saveToast.categories}</strong> categor
                {saveToast.categories === 1 ? 'y' : 'ies'}.
              </span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Stats */}
          <div className="flex gap-6 mb-4 text-sm">
            <div>
              <span className="font-bold text-ma-navy">{stats.total}</span>
              <span className="text-gray-500 ml-1">accounts</span>
            </div>
            <div>
              <span className="font-bold text-green-600">{stats.highConf}</span>
              <span className="text-gray-500 ml-1">high confidence</span>
            </div>
            <div>
              <span className="font-bold text-red-600">{stats.lowConf}</span>
              <span className="text-gray-500 ml-1">need review</span>
            </div>
          </div>

          {/* Filter + search */}
          <div className="flex flex-wrap gap-2 items-center">
            {(['all', 'low_confidence', 'mapped', 'unmapped'] as FilterMode[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
                style={{
                  background: filter === f ? '#001F8C' : '#F3F4F6',
                  color: filter === f ? 'white' : '#4B5563',
                }}
              >
                {f === 'all' ? 'All' :
                  f === 'low_confidence' ? 'Needs review' :
                    f === 'mapped' ? 'Mapped' : 'Unmapped'}
              </button>
            ))}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search code or name…"
              className="ml-auto px-3 py-1.5 border border-gray-300 rounded-lg text-sm flex-1 max-w-xs"
            />
          </div>
        </div>

        {/* ACCOUNT LIST */}
        <div className="bg-white rounded-2xl border border-gray-200 mb-6 overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
            <div className="col-span-1">Code</div>
            <div className="col-span-4">Name</div>
            <div className="col-span-2">Xero type</div>
            <div className="col-span-1 text-center">Conf</div>
            <div className="col-span-3">FFM category</div>
            <div className="col-span-1 text-right">Source</div>
          </div>

          {visibleAccounts.length === 0 ? (
            <div className="px-5 py-8 text-center text-gray-400 text-sm">
              No accounts match the current filter.
            </div>
          ) : (
            visibleAccounts.map((a) => {
              const sug = suggestions[a.accountId];
              const conf = sug?.confidence ?? 0;
              const source = sug?.source ?? 'default';
              const owner = isOwnerLike(a.name);
              const rowBg = conf < 0.65 ? '#FEF2F2' : conf < 0.85 ? '#FFFBEB' : 'white';
              return (
                <div
                  key={a.accountId}
                  className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-gray-100 items-center text-sm"
                  style={{ background: rowBg }}
                >
                  <div className="col-span-1 font-mono text-xs text-gray-600">{a.code || '—'}</div>
                  <div className="col-span-4">
                    <div className="text-ma-navy font-medium">{a.name}</div>
                    {owner && (
                      <div className="text-xs text-amber-700 mt-0.5">
                        Likely owner-related — check pension vs salary
                      </div>
                    )}
                  </div>
                  <div className="col-span-2">
                    <span className="inline-block px-2 py-0.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-600">
                      {a.type || '—'}
                    </span>
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <span
                      title={`${(conf * 100).toFixed(0)}% confidence`}
                      style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: confidenceColour(conf),
                        display: 'inline-block',
                      }}
                    />
                  </div>
                  <div className="col-span-3">
                    <select
                      value={mapping[a.accountId] || 'other_operating'}
                      onChange={(e) => setCategory(a.accountId, e.target.value as FFMCategory)}
                      className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white"
                    >
                      {FFM_CATEGORY_KEYS.map((k) => (
                        <option key={k} value={k}>{FFM_CATEGORIES[k].label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-1 text-right">
                    <span
                      className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide"
                      style={{ background: SOURCE_BG[source], color: SOURCE_FG[source] }}
                    >
                      {SOURCE_LABEL[source]}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* SUMMARY */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-4">
            Mapping summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {PL_GROUPS.map((g) => {
              const count = g.categories.reduce((sum, c) => sum + (groupCounts[c] || 0), 0);
              return (
                <div
                  key={g.key}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-100"
                  style={{ background: '#F9FAFB' }}
                >
                  <span className="text-sm text-gray-700">{g.label}</span>
                  <span className="text-sm font-bold text-ma-navy">{count}</span>
                </div>
              );
            })}
            <div
              className="flex items-center justify-between p-3 rounded-lg border border-gray-100"
              style={{ background: '#F9FAFB' }}
            >
              <span className="text-sm text-gray-700">Ignored</span>
              <span className="text-sm font-bold text-gray-500">{groupCounts['ignore'] || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
